/**
 * The policy shape, taken from the documents actually stored in Firestore.
 *
 * Taken from the DOCUMENTS, which cost a round of rework to learn. The model
 * class in the client this replaced described the shape that client wanted and
 * translated on the way in, so several field names differed on the wire.
 * Trusting the class produced a list that compiled, passed its tests, and
 * would have rendered an empty column for nearly every row against real data.
 *
 * Verified against the 211 policies in `orgs/braintree-clinic/policies`.
 */

/**
 * Stored status values, snake_case as written.
 *
 * These are the values that land in Firestore. The previous client's enum
 * names differed from them and serialized on the way in, which is why this
 * list is written from stored documents rather than from any model class.
 */
export const POLICY_STATUSES = [
  'draft',
  'in_review',
  'pending_review',
  'approved',
  'published',
  'retired',
  'archived',
  'superseded',
] as const;

/**
 * Where a published policy's NEXT version stands, when one is in progress.
 *
 * A revision keeps the policy published — staff go on reading the current
 * version — so this is a second axis, not a status. Null when nothing is in
 * progress.
 */
/** `approved`: the next version has cleared its chain and waits for its effective date. */
export type RevisionStatus = 'draft' | 'in_review' | 'approved' | null;

export type PolicyStatus = (typeof POLICY_STATUSES)[number];

export interface Policy {
  id: string;
  policyNumber: string;
  title: string;
  status: PolicyStatus;
  /**
   * From `departmentNames`, or the `department` snapshot object when that
   * array is absent — which it is on most documents.
   */
  departmentNames: string[];
  regulatoryStandardLabels: string[];
  /** From `categoryName`, or the `category` snapshot object. */
  categoryName: string | null;
  /** Resolved from `ownerId` against the org's members. Null when unresolvable. */
  ownerName: string | null;
  /**
   * The owner is no longer an active member, so nobody is looking after this
   * policy. Nothing is reassigned automatically; the Owner inactive view is
   * how an administrator finds these and hands each one on.
   */
  ownerHasLeft?: boolean;
  /** From `nextReviewDate`. Null is common and legitimate: a draft has none. */
  reviewDate: string | null;
  /** The next version's state, on a published policy. */
  revisionStatus: RevisionStatus;
}

export const STATUS_LABELS: Record<PolicyStatus, string> = {
  draft: 'Draft',
  in_review: 'In review',
  pending_review: 'In review',
  approved: 'Approved',
  published: 'Published',
  retired: 'Retired',
  archived: 'Archived',
  superseded: 'Superseded',
};

/** Which semantic token pair a status wears. */
export const STATUS_TONE: Record<PolicyStatus, 'ok' | 'info' | 'neutral'> = {
  draft: 'neutral',
  in_review: 'info',
  pending_review: 'info',
  approved: 'info',
  published: 'ok',
  retired: 'neutral',
  archived: 'neutral',
  superseded: 'neutral',
};

/** What the next version is doing, in words, or null when there is none. */
export function revisionLabel(policy: Pick<Policy, 'status' | 'revisionStatus'>): string | null {
  if (policy.status !== 'published' || !policy.revisionStatus) return null;
  if (policy.revisionStatus === 'approved') return 'New version approved, not yet effective';
  return policy.revisionStatus === 'in_review' ? 'New version in review' : 'New version drafted';
}

/** A review falling due inside this window is worth flagging. */
export const EXPIRING_WINDOW_DAYS = 30;

/**
 * Whole days until a published policy's review falls due. Negative once it
 * has passed, 0 on the day itself. Null when the question does not apply.
 *
 * Both sides are normalized to local midnight before differencing. The review
 * date is a calendar date with no time, so comparing it against the current
 * instant made a policy due TODAY read as overdue from one minute past
 * midnight — the difference is negative twelve hours by lunchtime, which
 * floors to -1. "Overdue" has to flip when the day ends, not at whatever hour
 * the page happens to be opened.
 */
export function daysUntilReview(policy: Policy, now = new Date()): number | null {
  if (policy.status !== 'published' || !policy.reviewDate) return null;

  const due = new Date(policy.reviewDate + 'T00:00:00');
  if (Number.isNaN(due.getTime())) return null;

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((due.getTime() - today.getTime()) / 86_400_000);
}

/** Due for review within the window, but not yet past it. */
export function isExpiring(policy: Policy, now = new Date()): boolean {
  const days = daysUntilReview(policy, now);
  return days !== null && days >= 0 && days <= EXPIRING_WINDOW_DAYS;
}

/** Past its review date. */
export function isOverdue(policy: Policy, now = new Date()): boolean {
  const days = daysUntilReview(policy, now);
  return days !== null && days < 0;
}

/**
 * What the status column shows, once the derived states are folded in.
 *
 * Neither "Expiring" nor "Overdue" is stored. A policy is published or it is
 * not; both of these are facts about today's date against the review date,
 * and they change without anybody editing the document. Storing them would
 * mean a nightly job to keep them true and a window in which they are not.
 *
 * They are separated because conflating them is a compliance error, not a
 * cosmetic one. A policy due in three weeks needs scheduling; a policy whose
 * review date passed in July is a finding a surveyor can write up today, and
 * the list has to say so in a different colour.
 */
export function displayStatus(
  policy: Policy,
  now = new Date(),
): { label: string; tone: 'ok' | 'info' | 'neutral' | 'warn' | 'danger' } {
  if (isOverdue(policy, now)) return { label: 'Overdue', tone: 'danger' };
  if (isExpiring(policy, now)) return { label: 'Expiring', tone: 'warn' };
  return { label: STATUS_LABELS[policy.status], tone: STATUS_TONE[policy.status] };
}
